import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Mui from '@mui/material';
import {
    IconSearch,
    IconX,
} from '@tabler/icons-react';

import { ReactComponent as ChatIllustration } from '@assets/icons/message-outlined.svg';
import TabsSelector from '@components/TabsSelector';
import { Button, Input, Text } from '@fragments';
import { getIntlLocale } from '@i18n';
import { useChat } from '@contexts/ChatContext';
import { openProfile } from '@helpers/profile';
import CurrentChat from './components/CurrentChat';
import Preview from './components/CurrentChat/components/Preview';
import './styles.scss';

function getInitials(name = '') {
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + (parts[parts.length - 1]?.[0] || '')).toUpperCase();
}

function formatTime(dateStr, t) {
    if (!dateStr) return '';
    const normalizedDate = typeof dateStr === 'string'
        ? dateStr.replace(' ', 'T')
        : dateStr;
    const date = new Date(normalizedDate);
    if (Number.isNaN(date.getTime())) return '';

    const now = new Date();
    const diffDays = Math.floor((now - date) / 86400000);

    if (diffDays === 0) {
        return date.toLocaleTimeString(getIntlLocale(), { hour: '2-digit', minute: '2-digit' });
    }

    if (diffDays < 7) {
        const days = [
            t('list.weekdays.sun'),
            t('list.weekdays.mon'),
            t('list.weekdays.tue'),
            t('list.weekdays.wed'),
            t('list.weekdays.thu'),
            t('list.weekdays.fri'),
            t('list.weekdays.sat'),
        ];
        return days[date.getDay()];
    }

    return date.toLocaleDateString(getIntlLocale(), { day: '2-digit', month: '2-digit' });
}

function parseMessageContent(content) {
    if (!content) return {};
    if (typeof content !== 'string') return content;

    try {
        return JSON.parse(content);
    } catch {
        return { text: content };
    }
}

function getLastMessagePreview(lastMessage, t) {
    if (!lastMessage) return { text: t('list.noMessage'), empty: true };

    const parsedMessage = typeof lastMessage === 'string'
        ? parseMessageContent(lastMessage)
        : lastMessage;
    const message = typeof parsedMessage === 'object'
        ? parsedMessage
        : { text: String(parsedMessage) };
    const content = parseMessageContent(message.content);
    const asset = message.asset ?? content.asset ?? {};
    const text = String(
        message.text
        ?? content.text
        ?? message.message
        ?? ''
    ).trim();
    const type = String(
        message.asset_type
        ?? message.message_type
        ?? message.type
        ?? content.asset_type
        ?? asset.type
        ?? ''
    ).toUpperCase();
    const attachmentLabels = {
        IMAGE: t('list.attachment.image'),
        PHOTO: t('list.attachment.image'),
        AUDIO: t('list.attachment.audio'),
        VOICE: t('list.attachment.audio'),
        VIDEO: t('list.attachment.video'),
        PDF: t('list.attachment.pdf'),
        XLS: t('list.attachment.spreadsheet'),
        XLSX: t('list.attachment.spreadsheet'),
        FILE: t('list.attachment.file'),
        DOCUMENT: t('list.attachment.file'),
    };
    const attachmentLabel = attachmentLabels[type]
        || (message.asset_url || asset.url || content.fileName ? t('list.attachment.file') : '');

    if (attachmentLabel) {
        return {
            text: text
                ? t('list.attachmentWithText', { label: attachmentLabel, text })
                : content.fileName || attachmentLabel,
            empty: false,
        };
    }

    if (text) return { text, empty: false };
    return { text: t('list.noMessage'), empty: true };
}

function isSameChat(first, second) {
    if (!first || !second) return false;

    const firstChatId = first.chat_id ?? first.id;
    const secondChatId = second.chat_id ?? second.id;
    if (firstChatId != null && secondChatId != null) {
        return String(firstChatId) === String(secondChatId);
    }

    if (first.user_id != null && second.user_id != null) {
        return String(first.user_id) === String(second.user_id);
    }

    return false;
}

function Avatar({ chat }) {
    const userId = chat.user_id ?? null;

    // The row is already a button (opens the chat); the avatar inside it is a
    // plain span so we don't nest focusables — click goes to the profile.
    function handleClick(e) {
        if (userId == null) return;
        e.stopPropagation();
        openProfile({ id: userId, nickname: chat.nickname });
    }

    return (
        <div
            className={`chat-conversation__avatar${userId != null ? ' chat-conversation__avatar--link' : ''}`}
            onClick={userId != null ? handleClick : undefined}
            title={userId != null ? chat.username : undefined}
        >
            {chat.avatar ? (
                <img src={chat.avatar} alt="" />
            ) : (
                <Text.label contrast={8}>{getInitials(chat.username || chat.email || '')}</Text.label>
            )}
        </div>
    );
}

function ChatRow({ chat, selected, onClick }) {
    const { t } = useTranslation('chat');
    const timeStr = chat.last_message
        ? formatTime(chat.last_message_at || chat.last_message?.created_at || chat.updated_at, t)
        : '';
    const hasUnread = chat.unread_messages > 0;
    const lastMessage = getLastMessagePreview(chat.last_message, t);

    return (
        <button
            type="button"
            className={`chat-conversation${selected ? ' chat-conversation--selected' : ''}${hasUnread ? ' chat-conversation--unread' : ''}`}
            onClick={onClick}
            aria-pressed={selected}
        >
            <Avatar chat={chat} />

            <div className="chat-conversation__content">
                <div className="chat-conversation__top">
                    <Text.label contrast={9} className="chat-conversation__name">
                        {chat.username || chat.email || t('common:entities.user')}
                    </Text.label>
                    {timeStr && <Text.caption contrast={5}>{timeStr}</Text.caption>}
                </div>

                <div className="chat-conversation__bottom">
                    <Text.caption contrast={6} className="chat-conversation__preview">
                        {lastMessage.empty ? <em>{lastMessage.text}</em> : lastMessage.text}
                    </Text.caption>
                    {hasUnread && (
                        <span className="chat-conversation__unread-badge">
                            <Text.caption contrast={1}>
                                {chat.unread_messages > 99 ? '99+' : chat.unread_messages}
                            </Text.caption>
                        </span>
                    )}
                </div>
            </div>
        </button>
    );
}

function SkeletonRow() {
    return (
        <div className="chat-conversation-skeleton" aria-hidden="true">
            <div className="chat-conversation-skeleton__avatar" />
            <div className="chat-conversation-skeleton__content">
                <div className="chat-conversation-skeleton__line chat-conversation-skeleton__line--name" />
                <div className="chat-conversation-skeleton__line chat-conversation-skeleton__line--message" />
            </div>
        </div>
    );
}

function EmptyChatState() {
    const { t } = useTranslation('chat');

    return (
        <div className="chat-empty-state">
            <div className="chat-empty-state__draw" aria-hidden="true">
                <ChatIllustration />
            </div>
            <Text.h4 weight={500} contrast={9}>{t('screen.emptyTitle')}</Text.h4>
            <Text.p contrast={6}>{t('screen.emptyDescription')}</Text.p>
        </div>
    );
}

export default function Chat() {
    const { t } = useTranslation('chat');
    const {
        close,
        chats,
        loading,
        activeChat,
        openChat,
        clearActiveChat,
    } = useChat();
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState('all');
    const [preview, setPreview] = useState(null);
    const tabsActionRef = useRef(null);

    const unreadTotal = chats.reduce((acc, chat) => acc + (chat.unread_messages || 0), 0);
    const filterTabs = useMemo(() => [
        { key: 'all', label: t('screen.tabAll') },
        {
            key: 'unread',
            label: t('screen.tabUnread'),
            countBadge: unreadTotal > 0 ? (unreadTotal > 99 ? '99+' : unreadTotal) : null,
        },
    ], [t, unreadTotal]);

    const filtered = useMemo(() => {
        let list = chats;

        if (filter === 'unread') list = list.filter((chat) => chat.unread_messages > 0);
        if (search.trim()) {
            const query = search.toLocaleLowerCase('pt-BR');
            list = list.filter((chat) => (
                chat.username?.toLocaleLowerCase('pt-BR').includes(query)
                || chat.email?.toLocaleLowerCase('pt-BR').includes(query)
            ));
        }

        return list;
    }, [chats, search, filter]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape' && !preview) close();
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [close, preview]);

    const handleScreenAnimationEnd = (event) => {
        if (event.target !== event.currentTarget) return;

        tabsActionRef.current?.updateIndicator();
        tabsActionRef.current?.updateScrollButtons();
    };

    return (
        <>
            <section
                id="chat-screen"
                className={`chat-screen${activeChat ? ' chat-screen--has-active' : ''}`}
                aria-label={t('screen.ariaLabel')}
                onAnimationEnd={handleScreenAnimationEnd}
            >
                <div className="chat-screen__layout">
                    <aside className="chat-screen__sidebar" aria-label={t('screen.listAriaLabel')}>
                        <div className="chat-screen__sidebar-header">
                            <Text.h2 contrast={9}>{t('screen.title')}</Text.h2>
                            <Button
                                icon={<IconX />}
                                variant="rounded"
                                color="overlay"
                                size="small"
                                onClick={close}
                                tooltip={t('screen.close')}
                                aria-label={t('screen.close')}
                                preventLoading
                            />
                        </div>

                        <div className="chat-screen__search">
                            <Input.Text
                                value={search}
                                onInput={setSearch}
                                placeholder={t('screen.searchPlaceholder')}
                                start={<IconSearch />}
                                inputSize="small"
                                className="chat-screen__search-input"
                                aria-label={t('screen.searchPlaceholder')}
                            />
                        </div>

                        <TabsSelector
                            actionRef={tabsActionRef}
                            className="chat-screen__filters"
                            tabs={filterTabs}
                            value={filter}
                            onChange={setFilter}
                        />

                        <div className="chat-screen__list">
                            {loading ? (
                                Array.from({ length: 6 }).map((_, index) => <SkeletonRow key={index} />)
                            ) : filtered.length ? (
                                filtered.map((chat) => (
                                    <ChatRow
                                        key={chat.id || chat.user_id}
                                        chat={chat}
                                        selected={isSameChat(activeChat, chat)}
                                        onClick={() => openChat(chat)}
                                    />
                                ))
                            ) : (
                                <div className="chat-screen__list-empty">
                                    <Text.p contrast={6}>{t('screen.listEmpty')}</Text.p>
                                </div>
                            )}
                        </div>
                    </aside>

                    <main className="chat-screen__stage">
                        {activeChat ? (
                            <div className="chat-screen__conversation">
                                <CurrentChat
                                    info={activeChat}
                                    handleOpenPreview={setPreview}
                                    onClose={clearActiveChat}
                                />
                            </div>
                        ) : (
                            <EmptyChatState />
                        )}
                    </main>
                </div>
            </section>

            {preview && (
                <Mui.Portal>
                    <Preview image={preview} onClose={() => setPreview(null)} />
                </Mui.Portal>
            )}
        </>
    );
}
